import React, { useEffect, useState } from 'react';
import { Badge, Box, Button, CircularProgress, Divider, IconButton, Menu, Typography, FormControlLabel, Checkbox } from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { clearCartLocally, fetchCart, removeItemFromCart, updateItemBuyerAccount } from '../../store/slices/cartSlice';
import api from '../../api/axios';
import { formatMoney } from '../utils/UnitConversion.utils';
import { toast } from 'react-toastify';
import SteamAccountUserSelect from '../utils/SteamAccountUserSelect';
import {SteamAccount, subtractFromWalletAfterBuy, setAuthState} from '../../store/slices/authSlice';
import { useDepositWithdrawModal } from './wallet/DepositWithdrawModalContext';
import { RARITY_COLORS, RarityGrade } from '../utils/RarityColors';
import {getAuthStateFromLocalStorage} from "../utils/LocalStorageAuth";

const NavbarCart = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { openModal } = useDepositWithdrawModal();

  const items = useSelector((state: RootState) => state.cart.items);
  const userBalanceMinor = useSelector((state: RootState) => state.auth.wallet?.walletTradeableAmountMinor || 0);
  const currencySymbol = useSelector((state: RootState) => state.currency.symbol);
  const availableAccounts = useSelector((state: RootState) => state.auth.steamAccounts || []);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(false);
  const [deletingOfferId, setDeletingOfferId] = useState<string | null>(null);
  const [agreed, setAgreed] = useState<boolean>(false);

  const open = Boolean(anchorEl);

  useEffect(() => {
    dispatch(fetchCart());
  }, [dispatch]);

  useEffect(() => {
    if (!open) {
      setAgreed(false);
    }
  }, [open]);

  const totalItems = items.length;
  const totalPriceMinor = items.reduce((acc: number, i) => acc + i.priceMinor, 0);
  const hasEnoughBalance = userBalanceMinor >= totalPriceMinor;

  const showAccountSelect = availableAccounts.filter((a) => a.tradeable).length > 1;

  const handleRemoveItem = async (offerId: string) => {
    setDeletingOfferId(offerId);
    try {
      await dispatch(removeItemFromCart(offerId)).unwrap();
    } catch (error) {
      toast.error('Failed to remove item');
    } finally {
      setDeletingOfferId(null);
    }
  };

  const handleClearCart = async () => {
    setLoading(true);
    try {
      // Trigger backend endpoint to delete user cart entries
      await api.delete('/cart');
      dispatch(clearCartLocally());
      toast.success('Cart cleared successfully');
    } catch (error) {
      toast.error('Failed to clear cart');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    if (!hasEnoughBalance) {
      const missingMinor = totalPriceMinor - userBalanceMinor;
      const missingUsd = missingMinor / 100;
      setAnchorEl(null);
      openModal('deposit', missingUsd);
      return;
    }

    if (!agreed || loading) return;

    setLoading(true);
    try {
      const checkoutPayload = items.map(item => ({
        offerId: item.offerId,
        buyerSelectedSteamAccountId: item.selectedBuyerSteamAccountId
      }));

      await api.post('/cart/checkout', checkoutPayload);

      dispatch(subtractFromWalletAfterBuy(totalPriceMinor));
      dispatch(clearCartLocally());

      const syncRes = await api.get('/sync');
      dispatch(setAuthState(getAuthStateFromLocalStorage(syncRes.data)));

      setAnchorEl(null);

      toast.success('Purchase successful!');

      window.dispatchEvent(new CustomEvent('offers-bought', { detail: { offerIds: items.map(i => i.offerId) } }));

    } catch (error: any) {
      toast.error(error?.response?.data ?? 'Checkout failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAccountChange = (offerId: string, account: SteamAccount | null) => {
    if (account) {
      dispatch(updateItemBuyerAccount({ offerId, accountId: account.id }));
      api.patch(`/cart/offer/${offerId}/account`, { buyerSelectedSteamAccountId: account.id }).catch(() => {
        toast.error('Failed to update steam account for this item');
      });
    }
  };

  return (
      <>
        <IconButton
            onClick={(e) => setAnchorEl(e.currentTarget)}
            className="w-10 h-10 text-neutral-400 hover:bg-neutral-800 transition-colors"
        >
          <Badge badgeContent={totalItems > 99 ? '99+' : totalItems} color="primary" invisible={totalItems === 0}>
            <ShoppingCartIcon className="text-[22px]"/>
          </Badge>
        </IconButton>

        <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={() => !loading && setAnchorEl(null)}
            disableScrollLock
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              paper: {
                elevation: 0,
                className: 'text-white mt-3 min-w-[420px] shadow-2xl',
                sx: {
                  overflow: 'visible',
                  bgcolor: '#262626',
                  border: '1px solid #404040',
                  borderRadius: '8px',
                  '&::before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 12,
                    height: 12,
                    bgcolor: '#262626',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                    borderLeft: '1px solid #404040',
                    borderTop: '1px solid #404040'
                  }
                }
              },
              list: { className: '!p-0' }
            }}
        >
          {items.length > 0 ? (
                  <Box className="flex flex-col max-h-[600px] relative z-10 select-none">
                    <Box className="px-4 pt-4 pb-2 flex justify-between items-center">
                      <Typography className="text-neutral-400 text-[10px] font-black uppercase tracking-[0.15em] leading-none">
                        {totalItems} {totalItems === 1 ? 'Item' : 'Items'} in Cart
                      </Typography>
                      <Button
                          size="small"
                          disabled={loading}
                          className="text-neutral-500 hover:text-white normal-case text-xs font-semibold"
                          onClick={handleClearCart}
                      >
                        Clear
                      </Button>
                    </Box>
                    <Divider className="mx-4 border-neutral-700/50" />

                    <div className="overflow-y-auto scrollbar-thin scrollbar-thumb-neutral-600 max-h-[340px]">
                      {items.map((item) => {
                        const currentAccount = availableAccounts.find(a => a.id === item.selectedBuyerSteamAccountId) || null;
                        const rarityColor = RARITY_COLORS[item.item.details?.rarity as RarityGrade] ?? '#374151';
                        const isST = item.item.details?.isStattrak;
                        const isSN = item.item.details?.isSouvenir;
                        const isDeleting = deletingOfferId === item.offerId;

                        const wear = item.item.catalog?.wearName ?? '';
                        const wearShort =
                            wear === 'FACTORY_NEW' ? 'FN' :
                                wear === 'MINIMAL_WEAR' ? 'MW' :
                                    wear === 'FIELD_TESTED' ? 'FT' :
                                        wear === 'WELL_WORN' ? 'WW' :
                                            wear === 'BATTLE_SCARRED' ? 'BS' : '';

                        return (
                            <div key={item.offerId} className="flex flex-col border-b border-white/5 hover:bg-neutral-700/20 transition-colors px-3 py-3">
                              <div className="flex items-center gap-3">
                                <div className="relative flex items-center shrink-0">
                                  <div style={{ width: 3, height: 44, background: rarityColor }}/>
                                  <div className="relative ml-1">
                                    <img src={item.item.catalog?.imageLink ?? ''} className="w-12 h-12 object-contain z-10 relative drop-shadow-md" alt=""/>
                                    <div
                                        style={{
                                          position: 'absolute',
                                          top: '50%',
                                          left: -4,
                                          transform: 'translateY(-50%)',
                                          width: 52,
                                          height: 44,
                                          background: `linear-gradient(to right, ${rarityColor}22, transparent)`,
                                          zIndex: 0
                                        }}
                                    />
                                  </div>
                                </div>

                                <Box className="flex-grow min-w-0">
                                  <Typography className="text-neutral-200 font-semibold text-[13px] leading-snug truncate">
                                    {item.item.catalog?.groupName ?? 'Unknown Item'}
                                  </Typography>
                                  <div className="flex items-center gap-1 mt-1">
                                    {isST && (
                                        <span className="px-1 py-0.5 bg-orange-600/20 border border-orange-500/30 rounded text-[8px] font-black uppercase tracking-wider text-orange-500">
                                          StatTrak™
                                        </span>
                                    )}
                                    {isSN && (
                                        <span className="px-1 py-0.5 bg-yellow-600/20 border border-yellow-500/30 rounded text-[8px] font-black uppercase tracking-wider text-yellow-500">
                                          Souvenir
                                        </span>
                                    )}
                                    {wearShort && (
                                        <span className="px-1 py-0.5 bg-neutral-700 border border-neutral-600 rounded text-[8px] font-black uppercase tracking-wider text-neutral-300">
                                          {wearShort}
                                        </span>
                                    )}
                                  </div>
                                </Box>

                                <Box className="text-right flex flex-col items-end shrink-0 gap-1.5 min-w-[75px]">
                                  <Typography className="font-bold text-[13px] text-white">
                                    {currencySymbol}{formatMoney(item.priceMinor / 100)}
                                  </Typography>
                                  <IconButton
                                      size="small"
                                      disabled={isDeleting || loading}
                                      onClick={() => handleRemoveItem(item.offerId)}
                                      className="text-neutral-500 hover:text-red-400 p-0.5 transition-colors"
                                  >
                                    {isDeleting ? <CircularProgress size={12} color="inherit" /> : <DeleteOutlineIcon className="text-sm" />}
                                  </IconButton>
                                </Box>
                              </div>

                              {showAccountSelect && (
                                  <Box className="mt-2.5 w-full">
                                    <div className="flex items-center gap-1.5 mb-1.5 px-1">
                                      <div className="w-1 h-2.5 bg-blue-500 rounded-full" />
                                      <span className="text-[9px] font-black uppercase tracking-[0.15em] text-neutral-400 leading-none">
                                        Steam Delivery Account
                                      </span>
                                    </div>
                                    <SteamAccountUserSelect
                                        value={currentAccount}
                                        size="small"
                                        variant="outlined"
                                        placeholder={false}
                                        showAvatar={false}
                                        onChange={(acc) => handleAccountChange(item.offerId, acc)}
                                    />
                                  </Box>
                              )}
                            </div>
                        );
                      })}
                    </div>

                    <Box className="p-4 bg-neutral-900/40 border-t border-white/5 relative z-10 flex flex-col gap-3.5 shadow-[0_-8px_24px_rgba(0,0,0,0.2)]">
                      {hasEnoughBalance && (
                          <div className="px-1">
                            <FormControlLabel
                                control={
                                  <Checkbox
                                      checked={agreed}
                                      disabled={loading}
                                      onChange={(e) => setAgreed(e.target.checked)}
                                      size="small"
                                      sx={{
                                        padding: '0px',
                                        color: '#737373',
                                        '&.Mui-checked': {
                                          color: '#3f51b5'
                                        }
                                      }}
                                  />
                                }
                                label={
                                  <span className="text-[12px] text-neutral-400 leading-none block select-none">
                                    I agree to the{' '}
                                    <a
                                        href="/tos"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-dark-primary hover:underline font-medium inline-block"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                      Terms of Service
                                    </a>{' '}
                                    &{' '}
                                    <a
                                        href="/refund-policy"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-dark-primary hover:underline font-medium inline-block"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                      Refund Policy
                                    </a>
                                  </span>
                                }
                                className="!items-center !m-0 gap-2.5 w-full"
                            />
                          </div>
                      )}

                      <div className="flex justify-between items-baseline px-1 pt-0.5">
                        <span className="text-xs font-black uppercase tracking-[0.1em] text-neutral-400">Total</span>
                        <div className="flex items-baseline gap-0.5">
                          <span className="text-sm font-bold text-neutral-200">{currencySymbol}</span>
                          <span className="text-xl font-black text-white tracking-tight">
                            {formatMoney(totalPriceMinor / 100)}
                          </span>
                        </div>
                      </div>

                      <Button
                          fullWidth
                          variant="contained"
                          disabled={loading || (hasEnoughBalance && !agreed)}
                          onClick={handleAction}
                          className="font-bold py-2 rounded-lg transition-all"
                      >
                        {loading ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : hasEnoughBalance ? (
                            t('cart.checkout')
                        ) : (
                            t('cart.deposit')
                        )}
                      </Button>
                    </Box>
                  </Box>
              ) :
              (
                  <Box className="flex flex-col items-center justify-center p-12 text-center select-none relative z-10 w-[355px] h-[260px]">
                    <div className="relative flex items-center justify-center w-20 h-20 bg-neutral-700/20 rounded-full border border-neutral-700/40 mb-5 text-neutral-500 shadow-inner">
                      <ShoppingBagOutlinedIcon className="text-[36px] text-neutral-400/80" />
                      <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-neutral-900 border border-neutral-700 rounded-full flex items-center justify-center text-[11px] font-black text-neutral-500 shadow-md">
                        0
                      </div>
                    </div>
                    <Typography className="text-neutral-500 !font-semibold text-xs max-w-[240px] leading-normal">
                      {t('cart.empty')}
                    </Typography>
                  </Box>
              )
          }
        </Menu>
      </>
  );
};

export default NavbarCart;